import Typography from '@material-ui/core/Typography';
import React, { useState, useMemo } from 'react';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';
import DeleteConfirmationDialog from '../../shared/components/DeleteConfirmationDialog';
import BackdropProgress from '../../shared/components/BackdropProgress';
import DataTable from '../../shared/components/DataTable';
import DataSetConfigMonitoringStatus from './DataSetConfigMonitoringStatus';
import { useDataSetMetadata, useRunIngestionApi } from './hooks';
import { useDeleteDataSetConfigApi } from '../hooks';
import { useErrorToast, useShowToastContext } from '../../shared/toast/ToastProvider';
import withDataSets from './datasets-hoc';

const useStyles = makeStyles((theme) => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(3),
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
    searchField: {
        minWidth: 280,
    },
}));

const columns = [
    {
        id: 'label',
        label: 'Label',
        minWidth: 100,
        align: 'left',
        sortable: true,
    },
    {
        id: 'name',
        label: 'Dataset',
        minWidth: 170,
        align: 'left',
        sortable: true,
    },
    {
        id: 'lastRunInfo',
        label: 'Ingestion Status',
        minWidth: 100,
        sortable: false,
    },
    {
        id: 'nextRunInfo',
        label: 'Next Run',
        maxWidth: 15,
        minWidth: 100,
        sortable: false,
    },
    {
        id: 'actions',
        label: 'Actions',
        align: 'right',
        minWidth: 170,
        sortable: false,
    }
];

const createCellRenderer = (
    isLoading,
    dataSourceStatus,
    dataSetMetadata,
    runIngestion,
    onDelete
) => (column, value, row) => {
    const { id } = column;
    const [open, setOpen] = React.useState(false);

    const text = (
        <>
            This will delete data set
            <b>
                {' '}
                { row.label && row.label.trim() !== '' ? row.label : row.name }
                {' '}
            </b>
            immediately. Are you sure you want to continue?
        </>
    );
    const handleCancel = (event) => {
        event.stopPropagation();
        setOpen(false);
    };
    const handleClick = (event) => {
        event.stopPropagation();
        setOpen(true);
    };

    const handleConfirm = (event) => {
        event.stopPropagation();
        onDelete(row.id);
    };

    const isNotPushMode = dataSetMetadata.mode !== 'PUSH';

    if (id === 'actions') {
        return (
            <>
                {isNotPushMode && (
                    <Button
                        type="button"
                        name="runIngestion"
                        variant="contained"
                        color="primary"
                        startIcon={<CloudDownloadIcon />}
                        disabled={isLoading}
                        onClick={(event) => {
                            event.stopPropagation();
                            runIngestion(row);
                        }}
                    >
                        Run Ingestion
                    </Button>
                )}

                <Button
                    variant="contained"
                    color="secondary"
                    style={{ marginLeft: '10px', padding: '8px' }}
                    startIcon={<DeleteIcon style={{ marginLeft: '10px' }} />}
                    onClick={handleClick}
                    disabled={false}
                />
                <DeleteConfirmationDialog
                    text={text}
                    open={open}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            </>
        );
    }

    if (id !== 'lastRunInfo') {
        return value;
    }

    return (
        <DataSetConfigMonitoringStatus
            isLoading={isLoading}
            dataSourceStatus={dataSourceStatus}
            dataSetId={row.id}
        />
    );
};

const DataSetConfigsListing = ({
    dataSource,
    dataSetConfigs,
    add,
    details,
    dataSourceStatus,
    onComplete,
    dataSets,
    isStatusLoading
}) => {
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState('');

    const handleDetails = (row) => {
        details(row);
    };
    const showAlert = useShowToastContext();

    const allRows = dataSetConfigs.map((cfg) => ({
        code: cfg.id,
        ...cfg
    }));

    const [
        { isSaving: isRunning, isError: isRunError, error: runError },
        onRunHandler,
    ] = useRunIngestionApi(() => {
        showAlert('Ingestion request has been send.', false);
    });

    const [{
        isSaving: isDeleting,
        isError: isDeleteError,
        error: deleteError
    }, onDataSetConfigDelete] = useDeleteDataSetConfigApi(() => {
        onComplete(true);
    });

    const runIngestion = async (row) => {
        console.log(dataSource);
        console.log(row.id);
        await onRunHandler({
            datasetId: Number(row.id),
            projectCode: dataSource.project,
            dataSourceCode: dataSource.code
        });
    };

    const handleDeleteConfig = async (id) => {
        const { project, code: dataSourceCode } = dataSource;
        await onDataSetConfigDelete({ project, dataSourceCode, id });
    };

    const dataSetMetadata = useDataSetMetadata(dataSets, allRows[0]?.name);

    const cellRenderer = React.useCallback(
        createCellRenderer(isStatusLoading, dataSourceStatus, dataSetMetadata,
            runIngestion, handleDeleteConfig),
        [isStatusLoading, dataSourceStatus, dataSetMetadata, runIngestion, handleDeleteConfig]
    );

    const filteredRows = useMemo(() => {
        if (!searchTerm.trim()) return allRows;
        const term = searchTerm.trim().toLowerCase();
        return allRows.filter((r) => (
            (r.label && r.label.toLowerCase().includes(term))
            || (r.name && r.name.toLowerCase().includes(term))
        ));
    }, [allRows, searchTerm]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    useErrorToast(isRunError, runError);
    useErrorToast(isDeleteError, deleteError);

    return (
        <Box>
            <Box className={classes.header}>
                <Typography variant="h4" noWrap>
                    Data Sets
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={add}
                >
                    Add New
                </Button>
            </Box>
            <Box className={classes.toolbar}>
                <TextField
                    className={classes.searchField}
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Label or dataset name…"
                />
            </Box>
            <BackdropProgress open={isRunning || isDeleting} />
            <DataTable
                columns={columns}
                rows={filteredRows}
                details={handleDetails}
                tableName="data-set-configs"
                enableSorting
                cellRenderer={cellRenderer}
                savedCron={allRows.map((row) => row.savedCron)}
                withoutAddButton
                disablePagination
            />
        </Box>
    );
};

export default withDataSets(DataSetConfigsListing);
